import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { AgentSettings, Economics } from "../../types.js";
import { resetLabel } from "../studioShared.js";
import { Chip } from "./atoms.js";
import { autonomyLabel, deriveState, type AgentState } from "./statusHelpers.js";

const STATE_META: Record<
  AgentState,
  { label: string; tint: (t: Theme) => string }
> = {
  running: { label: "Running", tint: (t) => t.palette.success.main },
  paused: { label: "Paused", tint: (t) => t.palette.warning.main },
  sleeping: { label: "Sleeping", tint: (t) => t.palette.info.main },
};

/** Hero: the at-a-glance state banner (running / paused / sleeping + mode chips). */
export function StateHero({
  settings,
  econ,
}: {
  settings: AgentSettings | undefined;
  econ: Economics | undefined;
}): JSX.Element {
  if (!settings) {
    return <Skeleton variant="rounded" height={120} />;
  }
  const state = deriveState(settings, econ);
  const meta = STATE_META[state];
  const autonomous =
    settings.auto_draft_enabled || settings.autonomy === "fully_auto";

  const subline =
    state === "paused"
      ? "Paused. Resume from the header (top-right) to let it work again."
      : state === "sleeping"
        ? `Daily AI budget reached — wakes ${econ ? resetLabel(econ.budget.resets_at) : "soon"}. You can still create posts manually.`
        : autonomous
          ? "Running autonomously on its schedule — discovering topics and drafting content."
          : "Running. It discovers topics for you; turn on autonomous drafting to let it write on its own.";

  return (
    <Card data-testid="status-hero">
      <CardContent sx={{ px: 2.5, py: 2.5, display: "grid", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            aria-hidden
            sx={{
              width: 14,
              height: 14,
              borderRadius: 999,
              bgcolor: (t) => meta.tint(t),
              boxShadow: (t) => `0 0 0 4px ${alpha(meta.tint(t), 0.18)}`,
            }}
          />
          <Typography
            data-testid="status-state"
            sx={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            {meta.label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {subline}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
          <Chip tint={(t) => t.palette.primary.main}>
            Mode · {autonomyLabel(settings.autonomy)}
          </Chip>
          <Chip
            tint={(t) =>
              autonomous ? t.palette.success.main : t.palette.text.secondary
            }
          >
            Autonomous drafting {autonomous ? "on" : "off"}
          </Chip>
          <Chip
            tint={(t) =>
              settings.publisher_enabled
                ? t.palette.success.main
                : t.palette.text.secondary
            }
          >
            Publishing {settings.publisher_enabled ? "on" : "safe-mode"}
          </Chip>
          <Chip tint={(t) => t.palette.info.main}>
            Model · {econ?.primary_model || "via AI Gateway"}
          </Chip>
          {settings.steer_prompt ? (
            <Chip tint={(t) => t.palette.secondary.main}>Steering active</Chip>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
